import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { History } from 'history'; // eslint-disable-line

import lang from '../../lib/language';
import { ENROLL_REQUEST_STATE } from '../../lib/models/enroll';
import {
  ENROLL_AUTH_REQUEST_TYPE,
  REQUEST_CALL_NAME_CONFIRM_EMAIL,
  REQUEST_CALL_NAME_GET_MANUAL_TOKEN,
} from '../../lib/models/enroll-requests';
import { RootState } from '../../lib/store';
import { enrollAuth } from '../../lib/store/auth';
import { login as serviceLogin } from '../../lib/store/common-actions';
import { getDefaultSelectableTradingPLanFromState, parseOnboardingErrorCode } from '../../lib/store/crm/helpers';
import { isMyPortalTrade } from '../../lib/util/auth/helpers';

import PortalLogoCarousel from '../../assets/img/portal_logo_carousel.svg';
import { ClickableDiv } from '../../components';
import Button from '../../components/Button/Button';
import { ButtonTypesEnum } from '../../components/Button/Button.types';
import EmailConfirmedModal from '../../components/Enroll/EmailConfirmedModal/EmailConfirmedModal';
import ExpiredVerificationModal from '../../components/Enroll/ExpiredVerificationModal/ExpiredVerificationModal';
import LoginCarousel from '../../components/LoginCarousel/LoginCarousel';
import PoweredBy from '../../components/PoweredBy/PoweredBy';
import PoweredByMobile from '../../components/PoweredBy/PoweredByMobile';
import { useIsMobile } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';

import styles from './Login.module.scss';

type Props = ReduxProps;

const Login = (props: Props): JSX.Element => {
  const {
    login,
    appLoaded,
    accessToken,
    responseStatus,
    responseStatusCode,
    requestCallName,
    tradingPlatformId,
    isAuthRefreshing,
    authenticate,
    defaultTradingPlatformId,
  } = props;
  const location = useLocation();
  const history = useHistory();

  const [emailConfirmedModal, setEmailConfirmedModal] = useState<boolean>(false);
  const [isVerifyLinkExpired, setIsVerifyLinkExpired] = useState<boolean>(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (location.search?.match(/token[=]/) && appLoaded) {
      const queryParams = new URLSearchParams(location.search);

      if (queryParams) {
        authenticate(
          queryParams.get('Email'),
          queryParams.get('token'),
          queryParams.get('FirstName'),
          queryParams.get('LastName'),
          queryParams.get('CountryOfResidence'),
        );
      }
    }
  }, [appLoaded, authenticate, location.search]);

  useEffect(() => {
    if (accessToken && tradingPlatformId) {
      setEmailConfirmedModal(true);
    }
  }, [accessToken, tradingPlatformId]);

  useEffect(() => {
    if (
      responseStatus
      && responseStatus === ENROLL_REQUEST_STATE.ERROR
      && parseOnboardingErrorCode(responseStatusCode!) === ENROLL_REQUEST_STATE.REQUEST_ERROR
      && (requestCallName === REQUEST_CALL_NAME_CONFIRM_EMAIL || requestCallName === REQUEST_CALL_NAME_GET_MANUAL_TOKEN)
    ) {
      setIsVerifyLinkExpired(true);
    }
  }, [requestCallName, responseStatus, responseStatusCode]);

  const handleSignup = () => {
    if (Object.keys(defaultTradingPlatformId).length === 0) {
      history.push(UI_ROUTES.error);
    } else {
      history.push(UI_ROUTES.firstEnroll);
    }
  };

  if (isAuthRefreshing) {
    return <div />;
  }

  return (
    <>
      <div className={styles.wrapperContainer}>
        <div className={styles.containerLogin}>
          <div className={styles.logo}>
            <img src={PortalLogoCarousel} alt="Capital" />
          </div>
          <div className={styles.carousel}>
            <LoginCarousel />
          </div>
          <div className={styles.buttonsAndPolicies}>
            <Button
              className={styles.loginBtn}
              variant={ButtonTypesEnum.Primary}
              id="loginButton"
              fullWidth
              onClick={() => login(history)}
            >
              {isMyPortalTrade() ? lang.mobileOnboardingLogIn().toUpperCase() : lang.mobileOnboardingLogIn()}
            </Button>
            {!isMyPortalTrade() && (
            <>
              <ClickableDiv onClick={handleSignup}>
                <Button
                  className={styles.signUpBtn}
                  variant={ButtonTypesEnum.Tertiary}
                  id="signUpButton"
                  fullWidth
                >
                  {lang.mobileOnboardingSignUp()}
                </Button>
              </ClickableDiv>
            </>
            )}
          </div>
        </div>
        {emailConfirmedModal && <EmailConfirmedModal isOpened={emailConfirmedModal} />}
        {isVerifyLinkExpired && <ExpiredVerificationModal />}
      </div>
      {isMobile ? <PoweredByMobile display /> : <PoweredBy display />}
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  login: (history: History) => {
    dispatch(serviceLogin('login'));
  },
  authenticate: (
    email: string | null,
    token: string | null,
    firstName: string | null,
    lastName: string | null,
    country: string | null,
  ) => {
    dispatch(enrollAuth({
      requestType: ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL, email, token, firstName, lastName, country,
    }));
  },
});

const mapStateToProps = (state: RootState) => ({
  appLoaded: state.app.loaded,
  isAuthRefreshing: state.auth.status === 'refreshing' && state.auth.refreshToken,
  accessToken: state.auth.enroll.accessToken,
  crmConnected: state.crm.connected,
  responseStatus: state.auth.enroll?.status,
  responseStatusCode: state.auth.enroll?.statusCode,
  requestCallName: state.auth.enroll?.requestCallName,
  tradingPlatformId: state.crm.tradingPlatformId,
  defaultTradingPlatformId: getDefaultSelectableTradingPLanFromState(state.crm),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Login);
